import { parseApiDate } from "./dateTime";

// Centralizes what used to be near-identical `new Intl.DateTimeFormat(...)`
// + null/invalid-date guards, duplicated (with small drifts — "numeric" vs
// "2-digit" day, "long" vs "short" month, "-" vs "—" fallback) across
// Support.tsx, MemberDetails.tsx, ApplicationReview.tsx, TeamRoles.tsx,
// AuditLog/lib/auditFormat.ts, PastEvents/components/ReportDetailsModal.tsx,
// and Gatherings/lib/eventFormat.ts. Each call site still controls its own
// exact display shape via `options` — this only centralizes the
// parse-guard-format skeleton, not one single fixed format.
export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  locale?: string;
  // Shown for a missing/unparseable value. Defaults to "—" (the more
  // common convention across call sites) — a couple of pages used "-"
  // instead, which they now pass explicitly to keep their exact output.
  fallback?: string;
}

const DEFAULT_LOCALE = "en-GB";
const DEFAULT_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};

export const formatDate = (
  value: string | number | Date | null | undefined,
  options: FormatDateOptions = {},
): string => {
  const { locale = DEFAULT_LOCALE, fallback = "—", ...intlOptions } = options;

  // Always rendered in the viewer's local timezone (no `timeZone` override
  // needed at call sites) — see lib/dateTime.ts for how API values parse.
  const date = parseApiDate(value);
  if (!date) return fallback;

  // The day/month/year default only applies when the caller hasn't asked
  // for a time-only format (hour/minute/second with no day/month/year of
  // its own) — merging both unconditionally would silently turn a
  // time-only request (e.g. `{ hour: "2-digit", minute: "2-digit" }`, used
  // by formatAuditTime/formatReportTime) into a full date-and-time string.
  const hasTimeOption = intlOptions.hour !== undefined || intlOptions.minute !== undefined || intlOptions.second !== undefined;
  const hasDateOption = intlOptions.day !== undefined || intlOptions.month !== undefined || intlOptions.year !== undefined;
  const formatOptions =
    hasTimeOption && !hasDateOption ? intlOptions : { ...DEFAULT_FORMAT_OPTIONS, ...intlOptions };

  try {
    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  } catch {
    return fallback;
  }
};
